// src/main.js
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import gsap from "gsap";



/**
 * --------------------------------------------------------------------------
 * 1. Scene, Camera, Renderer
 * --------------------------------------------------------------------------
 */
const canvas = document.querySelector("canvas.webgl");
const scene = new THREE.Scene();

// Renderer
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
const sizes = { width: window.innerWidth, height: window.innerHeight };
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// Camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.set(0, 0, 10);
scene.add(camera);

// Orbit Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.maxDistance = 25;

// Smooth zoom animation
function startZoomLoop() {
  gsap.to(camera.position, {
    z: 7,
    duration: 5,
    ease: "sine.inOut",
    yoyo: true,
    repeat: -1,
  });
}
startZoomLoop();


/**
 * --------------------------------------------------------------------------
 * 2. Textures
 * --------------------------------------------------------------------------
 */
const textureLoader = new THREE.TextureLoader();
const matcapTexture = textureLoader.load("/textures/matcaps/8.png");

// Background texture
const bgTexture = textureLoader.load("/textures/matcaps/way.jpg");
bgTexture.wrapS = THREE.RepeatWrapping;
bgTexture.wrapT = THREE.RepeatWrapping;
bgTexture.repeat.set(2, 2);

// Optional background pattern
const dotTexture = textureLoader.load("/textures/dot.jpg", (texture) => {
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(window.innerWidth / 100, window.innerHeight / 100);
  scene.background = texture;
});

/**
 * --------------------------------------------------------------------------
 * 3. Lights
 * --------------------------------------------------------------------------
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 1);
scene.add(ambientLight);

/**
 * --------------------------------------------------------------------------
 * 4. Donuts
 * --------------------------------------------------------------------------
 */
const donutGeometry = new THREE.TorusGeometry(0.5, 0.3, 16, 100);
const donutMaterial = new THREE.MeshMatcapMaterial({
  matcap: matcapTexture,
  side: THREE.DoubleSide,
});
const donuts = [];

for (let i = 0; i < 300; i++) {
  const donut = new THREE.Mesh(donutGeometry, donutMaterial);
  donut.position.set(
    (Math.random() - 0.5) * 40,
    (Math.random() - 0.5) * 40,
    (Math.random() - 0.5) * 30
  );
  donut.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
  const scale = Math.random();
  donut.scale.set(scale, scale, scale);
  scene.add(donut);
  donuts.push(donut);

  // Drop-in animation
  donut.position.y = 30 + Math.random() * 20;
  gsap.to(donut.position, {
    y: (Math.random() - 0.5) * 40,
    duration: 2,
    delay: i * 0.005,
    ease: "power3.out",
  });
}

/**
 * --------------------------------------------------------------------------
 * 5. Squares
 * --------------------------------------------------------------------------
 */
const squareGeometry = new THREE.BoxGeometry(1, 1, 1);
const squareMaterial = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });
const squares = [];

for (let i = 0; i < 1000; i++) {
  const square = new THREE.Mesh(squareGeometry, squareMaterial);
  square.position.set(
    (Math.random() - 0.5) * 50,
    (Math.random() - 0.5) * 50,
    (Math.random() - 0.5) * 40
  );
  square.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
  const scale = Math.random();
  square.scale.set(scale, scale, scale);
  scene.add(square);
  squares.push(square);
}

/**
 * --------------------------------------------------------------------------
 * 6. Text Creation Utility + Click Logic
 * --------------------------------------------------------------------------
 */
const fontLoader = new FontLoader();
const clickableTexts = []; // store clickable meshes

function createAnimatedText(
  text,
  position = new THREE.Vector3(0, 0, 0),
  size = 1,
  rotation = new THREE.Euler(0, 0, 0),
  url = null
) {
  fontLoader.load("/font/helvetiker_bold.typeface.json", (font) => {
    const textGeometry = new TextGeometry(text, {
      font,
      size,
      height: 1,
      curveSegments: 6,
      bevelEnabled: false,
    });
    textGeometry.center();

    const textMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture });
    const textMesh = new THREE.Mesh(textGeometry, textMaterial);
    textMesh.position.copy(position);
    textMesh.rotation.copy(rotation);
    textMesh.scale.set(0, 0, 0);
    scene.add(textMesh);

    // Animate scale & rotation
    gsap.to(textMesh.scale, {
      x: 1,
      y: 1,
      z: 0.007,
      duration: 2,
      ease: "back.out(1.7)",
      delay: 0.5,
    });

    gsap.to(textMesh.position, {
      y: position.y + 0.2,
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
    });

    // Add interactivity
    if (url) textMesh.userData.url = url;
    clickableTexts.push(textMesh);
  });
}

/**
 * --------------------------------------------------------------------------
 * 7. Hero Texts (in 3D positions)
 * --------------------------------------------------------------------------
 */
createAnimatedText(
  "Harshad Dongardive\nFull Stack Developer",
  new THREE.Vector3(0, 0, 0),
  1,
  new THREE.Euler(0,0, 0),
    "https://www.linkedin.com/in/harshad-dongardive-054643204/" // 👈 clickable link

);

createAnimatedText(
  "Creative 3D Enthusiast",
  new THREE.Vector3(-10, -2, 5),
  0.8,
  new THREE.Euler(0, -Math.PI / 3, 0),
    "https://github.com/Harshad313D" // 👈 clickable link
);

createAnimatedText(
  "Discover My 3D Space",
  new THREE.Vector3(10, 3, 5),
  0.9,
  new THREE.Euler(0, Math.PI / 3, 0),
  "https://hd-portfolio-kappa.vercel.app/" // 👈 clickable link
);
/**
 * --------------------------------------------------------------------------
 * 7.5 Social Media 3D Icons
 * --------------------------------------------------------------------------
 */
const socialIcons = [];
const iconMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture });

// Helper: create an icon mesh (different geometry)
function createIcon(shape, position, url) {
  let geometry;
  switch (shape) {
    case "box":
      geometry = new THREE.BoxGeometry(1, 1, 1);
      break;
    case "torus":
      geometry = new THREE.TorusGeometry(0.5, 0.2, 16, 100);
      break;
    case "sphere":
      geometry = new THREE.SphereGeometry(0.5, 32, 32);
      break;
    case "cone":
      geometry = new THREE.ConeGeometry(0.6, 1, 32);
      break;
    default:
      geometry = new THREE.BoxGeometry(1, 1, 1);
  }

  const mesh = new THREE.Mesh(geometry, iconMaterial.clone());
  mesh.position.copy(position);
  mesh.userData.url = url;
  mesh.scale.set(1.5, 1.5, 1.5);
  scene.add(mesh);

  // Add floating animation
  gsap.to(mesh.position, {
    y: position.y + 0.3,
    duration: 2 + Math.random(),
    yoyo: true,
    repeat: -1,
    ease: "sine.inOut",
  });

  socialIcons.push(mesh);
}

// 🎯 Create 4 social icons
createIcon("torus", new THREE.Vector3(-10, 5, -3), "https://www.instagram.com/harshad_313d/");
createIcon("box", new THREE.Vector3(-10, -5, -3), "https://github.com/Harshad313D");
createIcon("sphere", new THREE.Vector3(10, 5, -3), "https://www.linkedin.com/in/harshad-dongardive-054643204/");
createIcon("cone", new THREE.Vector3(10, -5, -2), "https://hd-portfolio-kappa.vercel.app/");



/**
 * --------------------------------------------------------------------------
 * 8. Click + Hover Detection
 * --------------------------------------------------------------------------
 */
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

let hoverHue = 0; // for cycling colors

function onMouseMove(event) {
  mouse.x = (event.clientX / sizes.width) * 2 - 1;
  mouse.y = -(event.clientY / sizes.height) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
const intersects = raycaster.intersectObjects([...clickableTexts, ...socialIcons]);

  if (intersects.length > 0) {
    document.body.style.cursor = "pointer";

    // Dynamic rainbow color
    hoverHue += 1; // increase hue
    const color = new THREE.Color(`hsl(${hoverHue % 360}, 100%, 50%)`);
    intersects[0].object.material.color.set(color);

  } else {
    document.body.style.cursor = "default";

    // Reset all clickable texts to white
    clickableTexts.forEach((t) => t.material.color.set(0xffffff));
  }
}


function onMouseClick(event) {
  mouse.x = (event.clientX / sizes.width) * 2 - 1;
  mouse.y = -(event.clientY / sizes.height) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
const intersects = raycaster.intersectObjects([...clickableTexts, ...socialIcons]);

  if (intersects.length > 0) {
    const clicked = intersects[0].object;
    if (clicked.userData.url) {
      window.open(clicked.userData.url, "_blank");
    }
  }
}

window.addEventListener("mousemove", onMouseMove);
window.addEventListener("click", onMouseClick);

/**
 * --------------------------------------------------------------------------
 * 9. Resize Handling
 * --------------------------------------------------------------------------
 */
window.addEventListener("resize", () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();
  renderer.setSize(sizes.width, sizes.height);
});


/**
 * --------------------------------------------------------------------------
 * 11. Music Play / Pause Button
 * --------------------------------------------------------------------------
 */
const musicBtn = document.getElementById("music-btn");
const bgMusic = document.getElementById("bg-music");

let isPlaying = false;

musicBtn.addEventListener("click", () => {
  if (!isPlaying) {
    bgMusic.play();
    musicBtn.classList.add("playing");
    musicBtn.textContent = "⏸️"; // change icon to pause
  } else {
    bgMusic.pause();
    musicBtn.classList.remove("playing");
    musicBtn.textContent = "🎵"; // revert to music icon
  }
  isPlaying = !isPlaying;
});

// Optional: lower volume for ambience
bgMusic.volume = 0.4;


/**
 * --------------------------------------------------------------------------
 * 10. Animation Loop
 * --------------------------------------------------------------------------
 */

function tick() {
  squares.forEach((square) => {
    square.rotation.y += 0.0015;
    square.rotation.x += 0.007;
  });
socialIcons.forEach((icon) => {
  icon.rotation.x += 0.01;
  icon.rotation.y += 0.02;
});

  controls.update();
  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}

tick();





// thiscode also works good 
// import * as THREE from 'three';
// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
// import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
// import gsap from 'gsap'
// /**
//  * Canvas and Scene Setup
//  */
// const canvas = document.querySelector('canvas.webgl');
// const scene = new THREE.Scene();


// const donuts = [];
// const squares = [];


// /**
//  * Renderer
//  */
// const renderer = new THREE.WebGLRenderer({ canvas });
// const sizes = { width: window.innerWidth, height: window.innerHeight };
// renderer.setSize(sizes.width, sizes.height);
// renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// /**
//  * Camera and Controls
//  */
// const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
// camera.position.set(0, 0, 10);
// scene.add(camera);

// function zoomLoop() {
//     gsap.to(camera.position, {
//         z: 7,
//         duration: 5,
//         ease: "sine.inOut",
//         yoyo: true,
//         repeat: -1
//     });
// }
// zoomLoop();


// const controls = new OrbitControls(camera, canvas);
// controls.enableDamping = true;

//    // minimum zoom-in distance
// controls.maxDistance = 25;  // maximum zoom-out distance


// /**
//  * Texture Loader
//  */

// const textureLoader = new THREE.TextureLoader();
// const matcapTexture = textureLoader.load('/textures/matcaps/8.png');
// const bgTexture = textureLoader.load('/textures/matcaps/way.jpg');
// bgTexture.wrapS = THREE.RepeatWrapping;
// bgTexture.wrapT = THREE.RepeatWrapping;
// bgTexture.repeat.set(2, 2); // Optional: tile effect

// const imageTexture = textureLoader.load('/images/myphoto.jpg');


// /**
//  * Donuts with Matcap
//  */
// const donutGeometry = new THREE.TorusGeometry(0.5, 0.3, 16, 100);
// const donutMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture, side: THREE.DoubleSide });

// for (let i = 0; i < 300; i++) {
//     const donut = new THREE.Mesh(donutGeometry, donutMaterial);
//     donut.position.set(
//         (Math.random() - 0.5) * 40,
//         (Math.random() - 0.5) * 40,
//         (Math.random() - 0.5) * 30
//     );
//     donut.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
//     const scale = Math.random();
//     donut.scale.set(scale, scale, scale);
//     scene.add(donut);
//     donuts.push(donut);
// }
// donuts.forEach((donut, i) => {
//     donut.position.y = 30 + Math.random() * 20; // Start far above
//     gsap.to(donut.position, {
//         y: (Math.random() - 0.5) * 40,
//         duration: 2,
//         delay: i * 0.005,
//         ease: "power3.out"
//     });
// });

// /**
//  * Squares with Background Texture
//  */
// const squareGeometry = new THREE.BoxGeometry(1, 1, 1);
// const squareMaterial = new THREE.MeshNormalMaterial({  side: THREE.DoubleSide });

// // Add light for StandardMaterial
// const ambientLight = new THREE.AmbientLight(0xffffff, 1);
// scene.add(ambientLight);

// for (let i = 0; i < 1000; i++) {
//     const square = new THREE.Mesh(squareGeometry, squareMaterial);
//     square.position.set(
//         (Math.random() - 0.5) * 50,
//         (Math.random() - 0.5) * 50,
//         (Math.random() - 0.5) * 40
//     );
//     square.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
//     const scale = Math.random();
//     square.scale.set(scale, scale, scale);
//     scene.add(square);
//     squares.push(square);
// }

// /**
//  * Font + TextGeometry
//  */

// let textMesh;

// const fontLoader = new FontLoader();
// // fontLoader.load('/font/helvetiker_bold.typeface.json', (font) => {
// //     const textGeometry = new TextGeometry( " Harshad Dongardive\n FullStack Developer ", {
// //         font,
// //         size: 1,
// //         height: 1,
// //         curveSegments: 6,
// //         bevelEnabled: false
// //     });

// //     textGeometry.center();
// //     const textMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture, side: THREE.DoubleSide });
// //     textMesh = new THREE.Mesh(textGeometry, textMaterial);
// //     textMesh.scale.set(1, 1, 0.007); // Makes depth ~0.1
// //     scene.add(textMesh);

 
// // });


// fontLoader.load('/font/helvetiker_bold.typeface.json', (font) => {
//     const textGeometry = new TextGeometry(" Harshad Dongardive\n FullStack Developer ", {
//         font,
//         size: 1,
//         height: 1,
//         curveSegments: 6,
//         bevelEnabled: false
//     });

//     textGeometry.center();
//     const textMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture });
//     textMesh = new THREE.Mesh(textGeometry, textMaterial);

//     // Start tiny and hidden
//     textMesh.scale.set(0, 0, 0);
//     textMesh.rotation.set(Math.PI, Math.PI / 2, 0); // Start with some twist
//     scene.add(textMesh);

//     // Animate scale and rotation
//     gsap.to(textMesh.scale, {
//         x: 1,
//         y: 1,
//         z: 0.007,
//         duration: 2,
//         ease: "back.out(1.7)",
//         delay: 0.5
//     });

//     gsap.to(textMesh.rotation, {
//         x: 0,
//         y: 0,
//         z: 0,
//         duration: 2,
//         ease: "expo.out",
//         delay: 0.5
//     });

   
// });




// /**
//  * Resize Handler
//  */
// window.addEventListener('resize', () => {
//     sizes.width = window.innerWidth;
//     sizes.height = window.innerHeight;
//     camera.aspect = sizes.width / sizes.height;
//     camera.updateProjectionMatrix();
//     renderer.setSize(sizes.width, sizes.height);
// });

// const textLoader = new THREE.TextureLoader();
// textLoader.load('/textures/dot.jpg', (dotTexture) => {
//   dotTexture.wrapS = THREE.RepeatWrapping;
//   dotTexture.wrapT = THREE.RepeatWrapping;

//   dotTexture.repeat.set(window.innerWidth / 100, window.innerHeight / 100); // Adjust based on pattern size

//   scene.background = dotTexture;
// });













// /**
//  * Animation Loop
//  */
// const tick = () => {
//     // Rotate text
//     // if (textMesh) {
//     //     textMesh.rotation.y += 0.01;
//     //     textMesh.rotation.x += 0.003;
//     // }

//     // scene.rotation.x += 0.001;
//     // scene.rotation.y += 0.001;

//     // Rotate each donut
//     // donuts.forEach((donut) => {
//     //     donut.rotation.x += 0.001;
//     //     donut.rotation.y += 0.005;
//     //     donut.rotation.z += 0.002;
//     //     donut.position.z += Math.sin(donut.rotation.x) * 0.01;
//     // });

  
    

//     // Floating text wobble
// if (textMesh) {
//     textMesh.position.y = Math.sin(Date.now() * 0.001) * 0.2;
// }

// // Donuts float



    
//     // Rotate each square
//     squares.forEach((square) => {
//         square.rotation.y += 0.0015;
//         square.rotation.x += 0.007;
//     });

//     controls.update();
//     renderer.render(scene, camera);
//     requestAnimationFrame(tick);
// };

// tick();
